import { Eye, Truck, ShoppingCart, Heart, Ticket, Percent } from "lucide-react";
import { Product } from "@/app/lib/features/product/product.types";
import {
  getSocialProofColor,
  getCampaignColor,
} from "@/app/utils/socialProofColors";
import { RotatingTextItem } from "@/app/components/common/sections/RotationText";
import FallbackImage from "@/app/components/elements/FallbackImage";

export const CAMPAIGN_ICON_MAP: Record<
  string,
  React.ComponentType<{ size: number; className?: string; style?: React.CSSProperties }>
> = {
  Heart,
  ShoppingCart,
  Eye,
  Truck,
  Ticket,
  Percent,
};

export interface NormalizedSize {
  id: string;
  name: string;
  stock: number;
  isAvailable: boolean;
}

export interface NormalizedVariant {
  id: number;
  color: string;
  colorCode: string;
  image: string;
  slug: string;
  isPopular: boolean;
}

/**
 * Normalize sizes from general_sizes
 *
 * general_sizes yalnızca beden ADLARINI taşır (string[]) — stok bilgisi
 * variants_by_color[].sizes içindedir. Bu yüzden stok oradan eşleştirilir;
 * aksi halde tükenmiş bedenler kartta seçilebilir görünür.
 *
 * Aynı beden birden fazla renkte bulunabildiği için stoklar toplanır: bir
 * renkte tükenmiş beden başka renkte varsa ürün kartında hâlâ mevcuttur.
 */
export const normalizeSizes = (product: Product): NormalizedSize[] => {
  if (!product.general_sizes || product.general_sizes.length === 0) {
    return [];
  }

  const stockBySize = new Map<string, number>();
  product.variants_by_color?.forEach((variant) => {
    variant.sizes?.forEach((size) => {
      stockBySize.set(
        size.size,
        (stockBySize.get(size.size) ?? 0) + (size.stock ?? 0),
      );
    });
  });

  return product.general_sizes.map((size) => {
    const stock = stockBySize.get(size);
    // Stok verisi hiç gelmemişse (API bu ürün için sizes göndermemiş) bedeni
    // kapatma — satılabilir ürünü yanlışlıkla tükenmiş göstermek daha kötü.
    return {
      id: size,
      name: size,
      stock: stock ?? 1,
      isAvailable: stock === undefined || stock > 0,
    };
  });
};

/**
 * Normalize variants from variants_by_color
 */
export const normalizeVariants = (product: Product): NormalizedVariant[] => {
  if (!product.variants_by_color || product.variants_by_color.length === 0) {
    return [];
  }

  return product.variants_by_color.map((variant) => ({
    id: variant.variant_id,
    color: variant.color_name,
    colorCode: variant.color_code,
    image: variant.primary_image || product.first_image,
    slug: variant.slug,
    isPopular: variant.is_popular,
  }));
};

export const buildSocialProofItems = (product: Product): RotatingTextItem[] => {
  if (!product.social_proof?.items || product.social_proof.items.length === 0) {
    return [];
  }

  return product.social_proof.items
    .filter((item) => item && item.text)
    .map((item) => {
      const iconElement = item.icon_full ? (
        <FallbackImage
          src={item.icon_full}
          alt={item.icon || 'icon'}
          width={12}
          height={12}
          className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0"
        />
      ) : (
        (() => {
          const IconComponent = CAMPAIGN_ICON_MAP[item.icon] || Eye;
          return <IconComponent size={10} className="shrink-0 sm:w-3 sm:h-3" />;
        })()
      );
      
      const colorStyle = item.color_hex ? { color: item.color_hex } : undefined;
      
      return {
        text: item.text,
        icon: iconElement,
        color: colorStyle ? undefined : getSocialProofColor(item),
        style: colorStyle,
      };
    });
};

export const buildCampaignItems = (product: Product): RotatingTextItem[] => {
  if (!product.campaigns || product.campaigns.length === 0) {
    return [];
  }

  return product.campaigns
    .filter((campaign) => campaign && campaign.name)
    .map((campaign) => {
      const iconElement = campaign.icon_full ? (
        <FallbackImage
          src={campaign.icon_full}
          alt={campaign.icon || 'icon'}
          width={12}
          height={12}
          className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0"
        />
      ) : (
        (() => {
          const IconComponent = CAMPAIGN_ICON_MAP[campaign.icon] || Ticket;
          return <IconComponent size={10} className="shrink-0 sm:w-3 sm:h-3" />;
        })()
      );
      
      const colorStyle = campaign.color_hex ? { color: campaign.color_hex } : undefined;
      
      return {
        text: campaign.name,
        icon: iconElement,
        color: colorStyle ? undefined : getCampaignColor(campaign),
        style: colorStyle,
      };
    });
};
